import React, { useEffect, useState } from "react";
import {
    ArrowBack,
    CancelOutlined,
    CheckCircleOutline,
    DeleteOutline,
    Edit,
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import {
    activateActivity,
    deleteActivity,
    listActivityDetails,
    resetActivityDetails,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { tokens } from "../../theme";
import AvailabilityUpdation from "../../components/AvailabilityUpdation";
import CustomConfirmModal from "../../components/CustomConfirmModal";
import ActivityFormModal from "./ActivityFormModal";
import { toActivityLines as toLines } from "../../utils";

function ActivityDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [editModal, setEditModal] = useState(false);

    const { activityDetails, isLoading } = useSelector(
        ({ loading, activities: { activityDetails } }) => ({
            activityDetails,
            isLoading: loading,
        })
    );

    useEffect(() => {
        document.title = 'Activity Details';
        dispatch(resetActivityDetails());
        fetchDetails();
        // eslint-disable-next-line
    }, [dispatch, id]);

    const fetchDetails = () => {
        dispatch(listActivityDetails({ id: id }));
    }

    const {
        name,
        description,
        rate,
        is_active,
        minimum_pax,
        maximum_pax,
        inclusions,
        exclusions,
        img,
    } = activityDetails || {};

    const loaded = !!activityDetails?._id;
    const showSkeleton = isLoading && !loaded;

    // updateActivity / activateActivity refresh the list, not this detail — re-fetch.
    const handleToggleAvailability = async () => {
        const ok = await dispatch(activateActivity({ id }));
        if (ok) fetchDetails();
    };

    const handleDelete = async () => {
        const ok = await dispatch(deleteActivity({ id }));
        if (ok) navigate("/activities");
    };

    const Stat = ({ label, value, hint }) => (
        <Box>
            <Typography variant="caption" sx={{ letterSpacing: 1 }} color={colors.grey[400]}>
                {label.toUpperCase()}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={colors.grey[100]}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color={colors.grey[400]}>
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );

    const ChecklistPanel = ({ title, lines, positive }) => (
        <Box
            p={2.5}
            height="100%"
            borderRadius={1}
            bgcolor={colors.primary[400]}
        >
            <Typography variant="caption" sx={{ letterSpacing: 1 }} color={colors.grey[400]}>
                {title.toUpperCase()}
            </Typography>
            {lines.length ? (
                <Stack spacing={1} mt={1.5}>
                    {lines.map((line, i) => (
                        <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                            {positive ? (
                                <CheckCircleOutline
                                    fontSize="small"
                                    sx={{ mt: "2px", color: colors.greenAccent[400] }}
                                />
                            ) : (
                                <CancelOutlined
                                    fontSize="small"
                                    sx={{ mt: "2px", color: colors.redAccent[400] }}
                                />
                            )}
                            <Typography color={colors.grey[200]}>{line}</Typography>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Typography mt={1.5} color={colors.grey[400]}>
                    None listed
                </Typography>
            )}
        </Box>
    );

    return (
        <Box m="20px" mb={15}>
            {/* Toolbar: back + record actions */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                spacing={2}
                m="20px 0"
            >
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ArrowBack fontSize="small" />}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Edit fontSize="small" />}
                        disabled={!loaded}
                        onClick={() => setEditModal(true)}
                        sx={{ fontWeight: 700 }}
                    >
                        Edit
                    </Button>
                    <CustomConfirmModal
                        text={`Delete "${name}"? This removes it from all listings.`}
                        submitBtnLabel="Delete"
                        action={handleDelete}
                        clickButton={(openConfirm) => (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutline fontSize="small" />}
                                disabled={!loaded}
                                onClick={openConfirm}
                                sx={{ fontWeight: 700 }}
                            >
                                Delete
                            </Button>
                        )}
                    />
                </Stack>
            </Stack>

            {showSkeleton ? (
                <Box p={3} borderRadius={1} bgcolor={colors.primary[400]}>
                    <Skeleton variant="text" width={120} />
                    <Skeleton variant="text" width="45%" height={48} />
                    <Skeleton variant="text" width="70%" />
                    <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />
                    <Stack direction="row" spacing={6}>
                        <Skeleton variant="text" width={90} height={48} />
                        <Skeleton variant="text" width={90} height={48} />
                        <Skeleton variant="text" width={90} height={48} />
                    </Stack>
                </Box>
            ) : (
                <>
                    {/* Hero */}
                    <Box p={3} borderRadius={1} bgcolor={colors.primary[400]}>
                        <Stack
                            direction={{ sm: "row", xs: "column" }}
                            justifyContent="space-between"
                            alignItems={{ sm: "flex-start", xs: "flex-start" }}
                            spacing={2}
                        >
                            <Box>
                                <Typography variant="caption" sx={{ letterSpacing: 2 }} color={colors.greenAccent[400]}>
                                    ACTIVITY
                                </Typography>
                                <Typography variant="h2" fontWeight="bold" color={colors.grey[100]}>
                                    {name || "-"}
                                </Typography>
                            </Box>
                            <Stack spacing={1} alignItems={{ sm: "flex-end", xs: "flex-start" }}>
                                <Chip
                                    label={is_active ? "Active" : "Inactive"}
                                    color={is_active ? "success" : "default"}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                                <AvailabilityUpdation
                                    name={name}
                                    isActive={is_active}
                                    action={handleToggleAvailability}
                                />
                            </Stack>
                        </Stack>

                        <Grid container spacing={3} mt={0.5} alignItems="flex-start">
                            <Grid item xs={12} md={img ? 7 : 12}>
                                {description ? (
                                    <Typography
                                        color={colors.grey[300]}
                                        sx={{ lineHeight: 1.7, whiteSpace: "pre-line" }}
                                    >
                                        {description}
                                    </Typography>
                                ) : (
                                    <Typography color={colors.grey[500]}>
                                        No description added.
                                    </Typography>
                                )}
                            </Grid>
                            {img ? (
                                <Grid item xs={12} md={5}>
                                    <Box
                                        component="img"
                                        src={img}
                                        alt={name || "activity"}
                                        sx={{
                                            width: "100%",
                                            maxHeight: 320,
                                            objectFit: "cover",
                                            borderRadius: 2,
                                            display: "block",
                                        }}
                                    />
                                </Grid>
                            ) : null}
                        </Grid>

                        <Divider sx={{ my: 3, borderColor: colors.grey[700] }} />

                        <Stack direction="row" spacing={6} flexWrap="wrap" useFlexGap>
                            <Stat label="Rate" value={`₹ ${Number(rate || 0).toFixed(2)}`} hint="per person" />
                            <Stat label="Min pax" value={minimum_pax ?? 1} />
                            <Stat label="Max pax" value={maximum_pax ? maximum_pax : "No cap"} />
                        </Stack>
                    </Box>

                    {/* Inclusions / Exclusions */}
                    <Grid container spacing={2} mt={0.5}>
                        <Grid item md={6} xs={12}>
                            <ChecklistPanel title="What's included" lines={toLines(inclusions)} positive />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <ChecklistPanel title="What's not included" lines={toLines(exclusions)} positive={false} />
                        </Grid>
                    </Grid>

                </>
            )}

            <ActivityFormModal
                open={editModal}
                onClose={() => setEditModal(false)}
                activity={activityDetails}
                onSuccess={fetchDetails}
            />
        </Box>
    );
}

export default ActivityDetails;
